import { useCallback, useEffect, useRef, useState } from "react";
import type { ChatMessage, ConnectionStatus, TextBlock } from "@/lib/chat-types";
import type { FundComplianceFileRef } from "@/lib/fund-compliance/api";
import { getFundComplianceSocketUrl } from "@/lib/fund-compliance/api";
import { createId } from "@/lib/fund-compliance/parse-primitives";
import { parseWsEvent } from "@/lib/fund-compliance/ws-events";
import type { QueuedMessage } from "@/hooks/useChatState";
import {
  createBuilder,
  wsEventHandlers,
  type HandlerContext,
} from "@/hooks/chat-socket-handlers";

/* ── Types ───────────────────────────────────────────────────────── */

interface SendMessageInput {
  content: string;
  mode: "qa" | "review";
  attachments?: FundComplianceFileRef[];
}

interface UseChatSocketOptions {
  chatId: string | null;
  skillId: string | null;
  initialMessages: ChatMessage[];
  queuedMessage?: QueuedMessage | null;
  onQueueFlushed?: () => void;
  onUnauthorized?: () => void;
  onHistoryLoaded?: (hasMore: boolean, nextBefore: string | null) => void;
}

/** Delay before auto-reconnecting after an unexpected close (ms). */
const RECONNECT_DELAY_MS = 2000;

/* ── Hook ────────────────────────────────────────────────────────── */

export function useChatSocket({
  chatId,
  skillId,
  initialMessages,
  queuedMessage,
  onQueueFlushed,
  onUnauthorized,
  onHistoryLoaded,
}: UseChatSocketOptions) {
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [status, setStatus] = useState<ConnectionStatus>(chatId && skillId ? "connecting" : "disconnected");
  const [connectionAttempt, setConnectionAttempt] = useState(0);
  // Ephemeral tool-use indicator — shows the latest toolName while the
  // server is calling tools, cleared on any non-tool_use event.
  const [toolUse, setToolUse] = useState<string | null>(null);

  /* ── Mutable refs ──────────────────────────────────────────────── */

  const socketRef = useRef<WebSocket | null>(null);
  const pendingFramesRef = useRef<string[]>([]);
  const builderRef = useRef(createBuilder());

  // Current subscription target — read by async handlers & onopen closure
  const currentChatIdRef = useRef(chatId);
  const currentSkillIdRef = useRef(skillId);
  currentChatIdRef.current = chatId;
  currentSkillIdRef.current = skillId;

  // Parent callbacks as refs so prop changes don't recycle the socket
  const onUnauthorizedRef = useRef(onUnauthorized);
  const onHistoryLoadedRef = useRef(onHistoryLoaded);
  const onQueueFlushedRef = useRef(onQueueFlushed);
  onUnauthorizedRef.current = onUnauthorized;
  onHistoryLoadedRef.current = onHistoryLoaded;
  onQueueFlushedRef.current = onQueueFlushed;

  // initialMessages via ref — reset effect must not re-run on parent re-render
  const initialMessagesRef = useRef(initialMessages);
  initialMessagesRef.current = initialMessages;

  /* ── Frame sending (stable refs) ───────────────────────────────── */

  const flushPendingFrames = useCallback(() => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    while (pendingFramesRef.current.length > 0) {
      const frame = pendingFramesRef.current.shift();
      if (frame) socket.send(frame);
    }
  }, []);

  const sendFrame = useCallback((frame: Record<string, unknown>) => {
    const serialized = JSON.stringify(frame);
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      pendingFramesRef.current.push(serialized);
      return;
    }
    socket.send(serialized);
  }, []);

  const requestReconnect = useCallback(() => {
    setConnectionAttempt((n) => n + 1);
  }, []);

  /* ── Prepend older history (REST pagination) ───────────────────── */

  const prependMessages = useCallback((older: ChatMessage[]) => {
    if (older.length === 0) return;
    setMessages((prev) => {
      const existingIds = new Set(prev.map((m) => m.id));
      const filtered = older.filter((m) => !existingIds.has(m.id));
      if (filtered.length === 0) return prev;
      return [...filtered, ...prev].sort((a, b) => a.timestamp - b.timestamp);
    });
  }, []);

  /* ── WebSocket lifecycle (independent of chatId) ───────────────── */

  useEffect(() => {
    if (!chatId || !skillId) {
      socketRef.current?.close();
      socketRef.current = null;
      setStatus("disconnected");
      return;
    }

    const socket = new WebSocket(getFundComplianceSocketUrl());
    socketRef.current = socket;
    setStatus("connecting");

    socket.onopen = () => {
      if (socketRef.current !== socket) return;
      setStatus("connected");

      // Subscribe to current chat (if any). Read from refs — these are the
      // latest values, regardless of when this effect was created.
      const subscribeChatId = currentChatIdRef.current;
      const subscribeSkillId = currentSkillIdRef.current;
      if (subscribeChatId && subscribeSkillId) {
        sendFrame({ type: "subscribe", chatId: subscribeChatId, skillId: subscribeSkillId });
      }

      flushPendingFrames();
    };

    socket.onmessage = (event) => {
      const parsed = parseWsEvent(event.data);
      if (!parsed) return;

      const currentChatId = currentChatIdRef.current;
      // Drop events for stale chat subscriptions
      if (parsed.chatId && currentChatId && parsed.chatId !== currentChatId) return;

      // Clear the ephemeral tool-use indicator on any non-tool_use event
      if (parsed.type !== "tool_use") {
        setToolUse(null);
      }

      const handlerCtx: HandlerContext = {
        chatId: currentChatId ?? "",
        builder: builderRef.current,
        setMessages,
        setToolUse,
        onUnauthorized: onUnauthorizedRef.current,
        onHistoryLoaded: onHistoryLoadedRef.current,
      };

      const handler = wsEventHandlers[parsed.type];
      if (handler) handler(parsed, handlerCtx);
    };

    socket.onclose = (closeEvent) => {
      if (socketRef.current !== socket) return;
      socketRef.current = null;
      if (closeEvent.code === 1008 || closeEvent.code === 4401 || closeEvent.code === 4403) {
        onUnauthorizedRef.current?.();
        return;
      }
      setStatus("disconnected");
      // Auto-reconnect after a short delay
      window.setTimeout(() => {
        setConnectionAttempt((n) => n + 1);
      }, RECONNECT_DELAY_MS);
    };

    socket.onerror = () => {
      if (socketRef.current !== socket) return;
      setStatus("error");
    };

    return () => {
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
      socket.close();
    };
  }, [chatId, connectionAttempt, flushPendingFrames, sendFrame, skillId]);

  /* ── Reset & subscribe on chat switch ──────────────────────────── */

  const lastSubscribedChatIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (lastSubscribedChatIdRef.current === chatId) return;
    lastSubscribedChatIdRef.current = chatId;

    // Reset per-chat local state
    setMessages(initialMessagesRef.current);
    setToolUse(null);
    Object.assign(builderRef.current, createBuilder());

    if (!chatId || !skillId) return;

    // Send subscribe if socket is already OPEN; otherwise onopen will handle it
    // (reading the latest chatId from the ref).
    const socket = socketRef.current;
    if (socket && socket.readyState === WebSocket.OPEN) {
      sendFrame({ type: "subscribe", chatId, skillId });
    }
  }, [chatId, skillId, sendFrame]);

  /* ── Process queued message (sent from empty state or after chat create) ─ */

  const lastProcessedQueueKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!queuedMessage || !chatId || !skillId) return;
    if (queuedMessage.chatId !== chatId) return;

    // Dedupe in case the effect re-runs before onQueueFlushed clears the prop
    const queueKey = `${queuedMessage.chatId}:${queuedMessage.payload.content}:${(queuedMessage.payload.attachments ?? []).map((a) => a.storageId).join(",")}`;
    if (lastProcessedQueueKeyRef.current === queueKey) return;
    lastProcessedQueueKeyRef.current = queueKey;

    const payload = queuedMessage.payload;
    const attachmentBlocks = (payload.attachments ?? []).map((att) => ({
      type: "attachment" as const,
      attachment: {
        filename: att.filename,
        filesize: att.filesize ?? "Uploaded file",
        filetype: att.mimeType.split("/").pop() ?? "file",
        status: payload.mode === "review" ? "scanning" as const : "uploaded" as const,
      },
    }));

    // Add optimistic user message
    setMessages((prev) => [
      ...prev,
      {
        id: createId("user"),
        role: "user",
        blocks: [
          { type: "text", content: payload.content } satisfies TextBlock,
          ...attachmentBlocks,
        ],
        _optimisticId: createId("optimistic"),
        timestamp: Date.now(),
      },
    ]);

    sendFrame({
      type: "user_message",
      chatId,
      skillId,
      mode: payload.mode,
      content: payload.content,
      attachments: (payload.attachments ?? []).map((att) => ({
        storageId: att.storageId,
        filename: att.filename,
        mimeType: att.mimeType,
        storageName: att.storageName,
      })),
    });

    onQueueFlushedRef.current?.();
  }, [chatId, skillId, queuedMessage, sendFrame]);

  /* ── Public send ───────────────────────────────────────────────── */

  const send = useCallback(
    (input: SendMessageInput) => {
      if (!chatId || !skillId) return;

      const trimmed = input.content.trim();
      if (!trimmed) return;

      const attachmentBlocks = (input.attachments ?? []).map((att) => ({
        type: "attachment" as const,
        attachment: {
          filename: att.filename,
          filesize: att.filesize ?? "Uploaded file",
          filetype: att.mimeType.split("/").pop() ?? "file",
          status: input.mode === "review" ? "scanning" as const : "uploaded" as const,
        },
      }));

      setMessages((prev) => [
        ...prev,
        {
          id: createId("user"),
          role: "user",
          blocks: [
            { type: "text", content: trimmed } satisfies TextBlock,
            ...attachmentBlocks,
          ],
          _optimisticId: createId("optimistic"),
          timestamp: Date.now(),
        },
      ]);

      const socket = socketRef.current;
      if (!socket || socket.readyState === WebSocket.CLOSING || socket.readyState === WebSocket.CLOSED) {
        requestReconnect();
      }

      sendFrame({
        type: "user_message",
        chatId,
        skillId,
        mode: input.mode,
        content: trimmed,
        attachments: (input.attachments ?? []).map((att) => ({
          storageId: att.storageId,
          filename: att.filename,
          mimeType: att.mimeType,
          storageName: att.storageName,
        })),
      });
    },
    [chatId, requestReconnect, sendFrame, skillId]
  );

  return { messages, status, send, prependMessages, toolUse };
}
